import React, { useState, useEffect } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogScrollArea,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  CheckCircle,
  Zap,
  Shield,
  Settings,
  Workflow,
  ExternalLink,
  Loader2,
  AlertCircle
} from 'lucide-react';
import { BrandIcons } from '../../ui/brand-icons';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { N8NSuccessModalContent } from './N8NSuccessModal';
import { AdvancedSocketSelection } from '../AdvancedSocketSelection';
import { integrationRegistry } from '../../../integrations/registry';
import { SpaceSocket } from '../../../services/api';
import { getOrCreateDefaultSocket } from '../../../utils/socketUtils';
import { buildBindingPermissions } from '../shared/bindingPayload';
import { MemoryPermissionsFields } from '../shared/MemoryPermissionsFields';
import { ContentFiltersFields } from '../shared/ContentFiltersFields';
import { RateLimitFields } from '../shared/RateLimitFields';

interface N8NIntegrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateIntegration: (integration: any) => Promise<any>;
  socket?: SpaceSocket;
  spaceId: string;
}

export default function N8NIntegrationModal({
  isOpen,
  onClose,
  onCreateIntegration,
  socket,
  spaceId
}: N8NIntegrationModalProps) {
  const { t } = useTranslation();
  const [integrationName, setIntegrationName] = useState('n8n Automation');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdIntegration, setCreatedIntegration] = useState<any>(null);
  const [selectedSocket, setSelectedSocket] = useState<SpaceSocket | null>(socket || null);
  const [socketLoading, setSocketLoading] = useState(false);
  const [socketError, setSocketError] = useState<string | null>(null);

  // Get or create default socket when modal opens
  useEffect(() => {
    if (isOpen && !socket && spaceId) {
      const loadDefaultSocket = async () => {
        setSocketLoading(true);
        setSocketError(null);
        try {
          const defaultSocket = await getOrCreateDefaultSocket(spaceId, integrationRegistry.n8n);
          setSelectedSocket(defaultSocket);
        } catch (error) {
          console.error('Failed to get or create default socket:', error);
          setSocketError(error instanceof Error ? error.message : 'Failed to load socket');
        } finally {
          setSocketLoading(false);
        }
      };
      loadDefaultSocket();
    } else if (socket) {
      setSelectedSocket(socket);
    }
  }, [isOpen, socket, spaceId]);

  // Permission states
  const [canRead, setCanRead] = useState(true);
  const [canWrite, setCanWrite] = useState(true);
  const [canSearch, setCanSearch] = useState(true);
  const [canDelete, setCanDelete] = useState(false);

  // Content filtering
  const [categories, setCategories] = useState('');
  const [excludeTags, setExcludeTags] = useState('');
  const [minImportance, setMinImportance] = useState(1);

  // Rate limiting
  const [requestsPerHour, setRequestsPerHour] = useState(10000);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!integrationName.trim() || !selectedSocket) return;

    setIsCreating(true);
    try {
      const permissions = buildBindingPermissions({
        flags: {
          can_read: canRead,
          can_write: canWrite,
          can_search: canSearch,
          can_delete: canDelete,
        },
        categoriesComma: categories,
        excludeTagsComma: excludeTags,
        minImportance,
        requestsPerHour,
      });

      const auth_config = {
        description: description.trim() || 'n8n automation platform integration',
        response_format: 'json'
      };

      const integrationData = {
        integration_type: 'n8n',
        integration_name: integrationName.trim(),
        permissions,
        auth_config,
        socket_id: selectedSocket.socket_id
      };

      const createdIntegration = await onCreateIntegration(integrationData);
      
      setCreatedIntegration({
        integration_name: integrationData.integration_name,
        binding_id: createdIntegration.binding_id,
        auth_config: {
          ...integrationData.auth_config,
          api_key: createdIntegration.auth_config.api_key
        },
        space_id: createdIntegration.space_id,
        permissions: integrationData.permissions
      });
      setShowSuccessModal(true);
      
    } catch (error) {
      console.error('Failed to create n8n integration:', error);
      toast.error(t('n8nIntegration.messages.creationFailed', { ns: 'integrations' }) as string);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setCreatedIntegration(null);
    
    // Reset form
    setIntegrationName('n8n Automation');
    setDescription('');
    setCanRead(true);
    setCanWrite(true);
    setCanSearch(true);
    setCanDelete(false);
    setCategories('');
    setExcludeTags('');
    setMinImportance(1);
    setRequestsPerHour(10000);
    
    onClose();
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (!open) {
      if (showSuccessModal) {
        handleSuccessModalClose();
      } else {
        onClose();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogOpenChange}>
      <DialogContent variant="scrollable" className="max-w-5xl bg-white">
        {showSuccessModal && createdIntegration ? (
          <N8NSuccessModalContent
            integration={createdIntegration}
            onClose={handleSuccessModalClose}
          />
        ) : (
          <>
        <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
          <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-3">
            <BrandIcons.n8n className="w-8 h-8" />
            {t('n8nIntegration.connectTitle', { ns: 'integrations' }) as string}
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            {selectedSocket ? (
              t('n8nIntegration.connectDescription', { ns: 'integrations' }) as string
            ) : (
              'Setting up your integration...'
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <DialogScrollArea className="space-y-6 px-6">
          {/* Integration Name */}
          <div>
            <label htmlFor="integrationName" className="block text-sm font-medium text-gray-700 mb-2">
              Integration Name *
            </label>
            <Input
              id="integrationName"
              type="text"
              value={integrationName}
              onChange={(e) => setIntegrationName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder="n8n Automation"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <Input
              id="description"
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              placeholder={t('n8nIntegration.descriptionPlaceholder', { ns: 'integrations' }) as string}
            />
          </div>

          {/* Permissions */}
          <Card className="border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                {t('n8nIntegration.permissions.title', { ns: 'integrations' }) as string}
              </CardTitle>
              <p className="text-sm text-gray-600">
                {t('n8nIntegration.permissions.description', { ns: 'integrations' }) as string}
              </p>
            </CardHeader>
            <CardContent>
              <MemoryPermissionsFields
                variant="embedded"
                value={{
                  can_read: canRead,
                  can_write: canWrite,
                  can_search: canSearch,
                  can_delete: canDelete,
                }}
                onChange={(v) => {
                  setCanRead(v.can_read);
                  setCanWrite(v.can_write);
                  setCanSearch(v.can_search);
                  setCanDelete(v.can_delete);
                }}
              />
            </CardContent>
          </Card>

          {/* Content Filtering */}
          <Card className="border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
                <Settings className="w-5 h-5 text-purple-600" />
                {t('n8nIntegration.contentFiltering.title', { ns: 'integrations' }) as string}
              </CardTitle>
              <p className="text-sm text-gray-600">
                {t('n8nIntegration.contentFiltering.description', { ns: 'integrations' }) as string}
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <ContentFiltersFields
                variant="embedded"
                categories={categories}
                excludeTags={excludeTags}
                minImportance={minImportance}
                onCategoriesChange={setCategories}
                onExcludeTagsChange={setExcludeTags}
                onMinImportanceChange={setMinImportance}
              />
            </CardContent>
          </Card>

          {/* Rate Limiting */}
          <Card className="border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-gray-900 flex items-center gap-2">
                <Zap className="w-5 h-5 text-orange-600" />
                {t('n8nIntegration.rateLimiting.title', { ns: 'integrations' }) as string}
              </CardTitle>
              <p className="text-sm text-gray-600">
                {t('n8nIntegration.rateLimiting.description', { ns: 'integrations' }) as string}
              </p>
            </CardHeader>
            <CardContent>
              <RateLimitFields
                requestsPerHour={requestsPerHour}
                onRequestsPerHourChange={setRequestsPerHour}
                min={1}
                max={50000}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('n8nIntegration.recommendedRequests', { ns: 'integrations' }) as string}
              </p>
            </CardContent>
          </Card>

          {/* Setup Preview */}
          <Card className="border-red-200 bg-red-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg text-red-900 flex items-center gap-2">
                <Workflow className="w-5 h-5" />
                What you'll get
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mt-0.5">
                    <CheckCircle className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-900">API Credentials</p>
                    <p className="text-xs text-red-700">{t('n8nIntegration.apiCredentials', { ns: 'integrations' }) as string}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mt-0.5">
                    <CheckCircle className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-900">{t('n8nIntegration.memoryOperations', { ns: 'integrations' }) as string}</p>
                    <p className="text-xs text-red-700">
                      {canRead && t('n8nIntegration.operations.read', { ns: 'integrations' }) as string}{canWrite && (canRead ? ', ' : '') + (t('n8nIntegration.operations.create', { ns: 'integrations' }) as string)}{canSearch && ((canRead || canWrite) ? ', ' : '') + (t('n8nIntegration.operations.search', { ns: 'integrations' }) as string)}{canDelete && ((canRead || canWrite || canSearch) ? ', ' : '') + (t('n8nIntegration.operations.delete', { ns: 'integrations' }) as string)} {t('n8nIntegration.operations.memories', { ns: 'integrations' }) as string}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mt-0.5">
                    <CheckCircle className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-900">{t('n8nIntegration.setupGuide', { ns: 'integrations' }) as string}</p>
                    <p className="text-xs text-red-700">{t('n8nIntegration.stepByStepInstallation', { ns: 'integrations' }) as string}</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center mt-0.5">
                    <CheckCircle className="w-4 h-4 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-red-900">{t('n8nIntegration.rateLimits', { ns: 'integrations' }) as string}</p>
                    <p className="text-xs text-red-700">{requestsPerHour.toLocaleString()} {t('n8nIntegration.requestsPerHour', { ns: 'integrations' }) as string}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Advanced Socket Selection */}
          {spaceId && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <AdvancedSocketSelection
                integration={integrationRegistry.n8n}
                spaceId={spaceId}
                currentSocket={selectedSocket}
                onSocketChange={setSelectedSocket}
                disabled={isCreating || socketLoading}
              />
              
              {/* Socket Loading State */}
              {socketLoading && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    <p className="text-blue-800 text-sm">Setting up socket...</p>
                  </div>
                </div>
              )}

              {/* Socket Error State */}
              {socketError && !socketLoading && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                    <div>
                      <p className="text-red-800 text-sm font-medium">Failed to load socket</p>
                      <p className="text-red-700 text-xs mt-1">{socketError}</p>
                      <p className="text-red-700 text-xs mt-1">You can create a socket manually in the advanced section above.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* External Links */}
          <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              asChild
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <a 
                href="https://www.npmjs.com/package/n8n-nodes-memara" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                {t('n8nIntegration.viewCommunityNode', { ns: 'integrations' }) as string}
              </a>
            </Button>
            
            <Button
              type="button"
              variant="ghost"
              size="sm"
              asChild
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <a 
                href="https://docs.n8n.io/integrations/community-nodes/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                {t('n8nIntegration.n8nDocumentation', { ns: 'integrations' }) as string}
              </a>
            </Button>
          </div>
          </DialogScrollArea>
          <DialogFooter variant="scrollable" className="gap-3 px-6 pb-6">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-gray-300"
            >
              {t('cancel', { ns: 'common' }) as string}
            </Button>
            <Button
              type="submit"
              disabled={isCreating || !integrationName.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isCreating ? t('n8nIntegration.creating', { ns: 'integrations' }) as string : t('n8nIntegration.create', { ns: 'integrations' }) as string}
            </Button>
          </DialogFooter>
        </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
} 